/* eslint no-console: 0 */
/* global axios */
/* eslint no-undef: "error" */

import Cookies from 'js-cookie';
import endPoints from './endPoints';
import {
  setAuthCredentials,
  clearCookiesOnLogout,
  setUser,
  getHeaderExpiry,
} from '../store/utils/api';

export default {
  parseCookieJSON(key) {
    const raw = Cookies.get(key);
    if (!raw) return null;
    const tryParse = value => {
      try {
        return JSON.parse(value);
      } catch (e) {
        return null;
      }
    };
    return tryParse(raw) || tryParse(decodeURIComponent(raw));
  },
  login(creds) {
    return new Promise((resolve, reject) => {
      axios
        .post('auth/sign_in', creds)
        .then(response => {
          // Persist auth headers immediately so subsequent calls include them
          setAuthCredentials(response);
          // Also set axios defaults right away
          const h = response.headers;
          if (h['access-token'] && h.client && h.uid) {
            axios.defaults.headers.common['access-token'] = h['access-token'];
            axios.defaults.headers.common.client = h.client;
            axios.defaults.headers.common.uid = h.uid;
            axios.defaults.headers.common.expiry = h.expiry;
            axios.defaults.headers.common['token-type'] = h['token-type'];
          }
          // If body is missing, seed a minimal user from headers so UI can move on
          const payload = response?.data?.data;
          if (!payload || typeof payload !== 'object') {
            const expiryDate = getHeaderExpiry(response);
            setUser(
              {
                uid: h.uid,
                email: h.uid,
                account_id: h['account-id'],
                accounts: [],
              },
              expiryDate,
            );
          }
          resolve(response);
        })
        .catch(error => {
          reject(error.response);
        });
    });
  },

  register(creds) {
    const urlData = endPoints('register');
    const fetchPromise = new Promise((resolve, reject) => {
      axios
        .post(urlData.url, {
          account_name: creds.accountName.trim(),
          user_full_name: creds.fullName.trim(),
          email: creds.email,
          password: creds.password,
        })
        .then(response => {
          setAuthCredentials(response);
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
    });
    return fetchPromise;
  },
  validityCheck() {
    const urlData = endPoints('validityCheck');
    return axios.get(urlData.url);
  },

  trimUrl(url) {
    const urlObj = new URL(url);
    return `${urlObj.protocol}//${urlObj.host}`;
  },

  setUserData(id) {
    if (!id) return Promise.resolve();
    const url_endpoint = `/api/v1/accounts/${id}/agents/set_user_data`;
    return axios.post(url_endpoint, {
      account_id: id,
    }, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    });
  },

  logout() {
    const urlData = endPoints('logout');
    const fetchPromise = new Promise((resolve, reject) => {
      axios
        .delete(urlData.url)
        .then(response => {
          clearCookiesOnLogout();
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
    });
    return fetchPromise;
  },

  isLoggedIn() {
    const authData = this.parseCookieJSON('auth_data');
    const userData = this.parseCookieJSON('user');
    return !!authData && !!userData;
  },

  isAdmin() {
    const user = this.parseCookieJSON('user');
    return user?.role === 'administrator';
  },

  getAuthData() {
    return this.parseCookieJSON('auth_data');
  },
  getPubSubToken() {
    const user = this.parseCookieJSON('user') || {};
    return user.pubsub_token || null;
  },
  getCurrentUser() {
    return this.parseCookieJSON('user');
  },

  verifyPasswordToken({ confirmationToken }) {
    return new Promise((resolve, reject) => {
      axios
        .post('auth/confirmation', {
          confirmation_token: confirmationToken,
        })
        .then(response => {
          setAuthCredentials(response);
          resolve(response);
        })
        .catch(error => {
          reject(error.response);
        });
    });
  },

  setNewPassword({ resetPasswordToken, password, confirmPassword }) {
    return new Promise((resolve, reject) => {
      axios
        .put('auth/password', {
          reset_password_token: resetPasswordToken,
          password_confirmation: confirmPassword,
          password,
        })
        .then(response => {
          setAuthCredentials(response);
          resolve(response);
        })
        .catch(error => {
          reject(error.response);
        });
    });
  },

  resetPassword({ email }) {
    const urlData = endPoints('resetPassword');
    return axios.post(urlData.url, { email });
  },

  profileUpdate({
    password,
    password_confirmation,
    displayName,
    ...profileAttributes
  }) {
    const formData = new FormData();
    Object.keys(profileAttributes).forEach(key => {
      const value = profileAttributes[key];
      if (value) {
        formData.append(`profile[${key}]`, value);
      }
    });
    formData.append('profile[display_name]', displayName || '');
    if (password && password_confirmation) {
      formData.append('profile[password]', password);
      formData.append('profile[password_confirmation]', password_confirmation);
    }
    return axios.put(endPoints('profileUpdate').url, formData)
  },

  updateUISettings({ uiSettings }) {
    return axios.put(endPoints('profileUpdate').url, {
      profile: { ui_settings: uiSettings },
    });
  },

  updateAvailability(availabilityData) {
    return axios.post(endPoints('availabilityUpdate').url, {
      profile: { ...availabilityData },
    });
  },
};
